import { useEffect, useState } from "react";
import { useCookies } from "react-cookie";
import { useLocation } from "react-router";
import useAlacrity from "@hooks/useAlacrity";
import { ARTICLE_ERROR_GENERIC } from "@pages/Article/components/ArticleSubshare/constants";
import {
  LAMBDA_RESPONSE_TYPES,
  LambdaResponsePayload,
} from "@pages/Article/types/Alacrity";
import { ArticlePaidModeType } from "@pages/Article/utils/enums";
import { DRUPAL_API_ENDPOINT } from "@util/constant";

type ArticleGiftReceivedProps = {
  paidMode?: ArticlePaidModeType;
};

/**
 * Custom hook to handle article gift received logic.
 */
export default function useArticleGiftReceived({
  paidMode,
}: ArticleGiftReceivedProps) {
  const { search, pathname } = useLocation();
  const giftToken =
    new URLSearchParams(search).get("gift") ??
    new URLSearchParams(search).get("token");
  const articleUrl = `${DRUPAL_API_ENDPOINT}${pathname}`;

  const [cookies] = useCookies(["neuronId"]);
  const neuronId: string | undefined = cookies.neuronId;

  const [isModalOpen, setIsModalOpen] = useState(true);
  const [alacrityRes, setAlacrityRes] = useState<LambdaResponsePayload | null>(
    null
  );
  const { verifyGiftToken } = useAlacrity();
  const isGiftReceived =
    alacrityRes?.data.responseType ===
    LAMBDA_RESPONSE_TYPES.SUCCESS_VALID_GIFT_TOKEN;

  useEffect(() => {
    if (paidMode !== ArticlePaidModeType.PREMIUM) return;
    if (!giftToken) return;

    const checkGiftReceived = async () => {
      if (!neuronId) {
        return;
      }

      try {
        const response = await verifyGiftToken(articleUrl, giftToken, neuronId);

        setAlacrityRes(response);
      } catch (err) {
        console.error("[subshare] GiftReceived err", err); // eslint-disable-line no-console
        setAlacrityRes(ARTICLE_ERROR_GENERIC);
      } finally {
        setIsModalOpen(true);
      }
    };

    checkGiftReceived();
  }, [giftToken, neuronId, verifyGiftToken, articleUrl, paidMode]);

  return { isModalOpen, setIsModalOpen, alacrityRes, isGiftReceived };
}
